import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { getThumbnailUrl } from '../lib/utils';
import { 
  Sparkles,
  BookOpen, 
  Award, 
  ChevronRight,
  Heart,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
const API = `${API_BASE}/api`;

export function CourseRecommendations() {
  const { token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [basedOn, setBasedOn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wishlistStatus, setWishlistStatus] = useState({});

  useEffect(() => {
    if (token) {
      fetchRecommendations();
    }
  }, [token]);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/user/recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
      setBasedOn(response.data.based_on);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleWishlist = async (courseId) => {
    try {
      if (wishlistStatus[courseId]) {
        await axios.delete(`${API}/user/wishlist/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlistStatus(prev => ({ ...prev, [courseId]: false }));
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/user/wishlist/${courseId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlistStatus(prev => ({ ...prev, [courseId]: true }));
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-navigate-gold animate-spin" />
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-navigate-gold" />
            Recommended For You
          </h3>
          <Link to="/courses" className="text-sm text-navigate-gold hover:underline flex items-center gap-1">
            View All
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          Based on your role profile, trending courses and learning goals
        </p>
      </div>

      {/* Recommendations Grid */}
      <div className="p-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {recommendations.slice(0, 6).map(course => (
            <div 
              key={course.course_id}
              className="bg-zinc-900/50 border border-zinc-700 rounded-xl overflow-hidden hover:border-zinc-600 transition-colors group"
            >
              {/* Thumbnail */}
              <div className="aspect-video bg-zinc-800 relative overflow-hidden">
                {course.thumbnail_url ? (
                  <img 
                    src={getThumbnailUrl(course.thumbnail_url)}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="h-10 w-10 text-zinc-600" />
                  </div>
                )}
                
                {/* Wishlist Button */}
                <button
                  onClick={() => toggleWishlist(course.course_id)}
                  className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                >
                  <Heart 
                    className={`h-4 w-4 ${wishlistStatus[course.course_id] ? 'text-red-400 fill-red-400' : 'text-white'}`} 
                  />
                </button>
              </div>

              {/* Content */}
              <div className="p-3">
                <span className="text-xs text-navigate-gold">{course.category}</span>
                <h4 className="text-white font-medium mt-1 line-clamp-2 text-sm">
                  {course.title}
                </h4>
                
                {course.recommendation_reason && (
                  <p className="text-xs text-zinc-500 mt-2 line-clamp-1">
                    {course.recommendation_reason}
                  </p>
                )}

                <Link 
                  to={`/courses/${course.slug || course.course_id}`}
                  className="inline-flex items-center text-xs text-navigate-gold hover:underline mt-3"
                >
                  View Course
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Wishlist Button Component for use in course cards
export function WishlistButton({ courseId, className = '' }) {
  const { token } = useAuth();
  const [inWishlist, setInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token && courseId) {
      checkWishlistStatus();
    }
  }, [token, courseId]);

  const checkWishlistStatus = async () => {
    try {
      const response = await axios.get(`${API}/user/wishlist/check/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInWishlist(response.data.in_wishlist);
    } catch (error) {
      console.error('Failed to check wishlist status:', error);
    }
  };

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!token) {
      toast.error('Please log in to add to wishlist');
      return;
    }

    setLoading(true);
    try {
      if (inWishlist) {
        await axios.delete(`${API}/user/wishlist/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/user/wishlist/${courseId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={toggleWishlist}
      disabled={loading}
      className={`p-2 rounded-full transition-colors ${
        inWishlist 
          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
          : 'bg-black/50 text-white hover:bg-black/70'
      } ${className}`}
      title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Heart className={`h-4 w-4 ${inWishlist ? 'fill-red-400' : ''}`} />
      )}
    </button>
  );
}
